import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/feature/AppLayout";
import { fetchInboxSearchConversations } from "@/api/inboxAgentApi";
import { SessionSearchQueryController } from "@/pages/search/SessionSearchQueryController";
import SearchTranscriptModal from "@/pages/search/SearchTranscriptModal";

function ownerLabel(assignee) {
  const raw = String(assignee || "").trim();
  if (!raw || raw.toLowerCase() === "unassigned") return "Unassigned";
  return raw;
}

function statusLabel(status) {
  const s = String(status || "open").trim();
  if (!s) return "open";
  return s.replace(/_/g, " ");
}

export default function SearchPage() {
  const controller = useMemo(
    () =>
      new SessionSearchQueryController({
        searchFn: ({ q, cursor }) =>
          fetchInboxSearchConversations({
            q,
            view: "search-all",
            scope: "central",
            cursor,
            limit: 50,
          }),
      }),
    [],
  );

  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => () => controller.cancel(), [controller]);

  const applyFirstPage = useCallback((page) => {
    setRows(Array.isArray(page?.data) ? page.data : []);
    setNextCursor(page?.nextCursor ?? null);
    setError("");
    setLoading(false);
  }, []);

  const applyError = useCallback((err) => {
    setError(err instanceof Error ? err.message : "Search failed.");
    setRows([]);
    setNextCursor(null);
    setLoading(false);
    setLoadingMore(false);
  }, []);

  const onQueryChange = (value) => {
    setQuery(value);
    if (!controller.isQueryable(value)) {
      controller.cancel();
      setRows([]);
      setNextCursor(null);
      setError("");
      setLoading(false);
      return;
    }
    setLoading(true);
    controller.schedule(value, applyFirstPage, applyError);
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    await controller.loadMore(
      query,
      nextCursor,
      (page) => {
        const extra = Array.isArray(page?.data) ? page.data : [];
        setRows((prev) => [...prev, ...extra]);
        setNextCursor(page?.nextCursor ?? null);
        setLoadingMore(false);
      },
      (err) => {
        setError(err instanceof Error ? err.message : "Could not load more results.");
        setLoadingMore(false);
      },
    );
  };

  const queryable = controller.isQueryable(query);
  const emptyHint = !queryable
    ? "Enter at least 2 characters to search sessions across every workspace you can access."
    : loading
      ? "Searching…"
      : error
        ? error
        : "No sessions matched that search.";

  return (
    <AppLayout>
      <div className="mx-auto flex h-full min-h-0 w-full max-w-5xl flex-col px-4 py-6 sm:px-6">
        <header className="mb-5 shrink-0">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Search</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Find bot and human sessions by ID, visitor, message, owner, or ticket.
          </p>
          <label className="mt-4 block">
            <span className="sr-only">Search sessions</span>
            <input
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Session ID, visitor, message, owner, or ticket"
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 shadow-sm outline-none ring-teal-500/30 placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </label>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && queryable ? (
            <p className="py-10 text-center text-sm text-slate-500">Searching…</p>
          ) : !rows.length ? (
            <p className="py-10 text-center text-sm text-slate-500">{emptyHint}</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((row) => (
                <li key={`${row.workspaceId || ""}:${row.id}`}>
                  <button
                    type="button"
                    onClick={() => setSelected(row)}
                    className="w-full rounded-xl border border-slate-200 bg-white p-3.5 text-left shadow-sm transition hover:border-teal-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-900"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-50">
                          {row.user?.name || "Visitor"}
                        </p>
                        <p className="mt-0.5 truncate font-mono text-[11px] text-slate-500">
                          {row.id}
                        </p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {statusLabel(row.status)}
                      </span>
                    </div>
                    <p className="mt-2 line-clamp-2 text-xs text-slate-600 dark:text-slate-300">
                      {row.preview || "—"}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                      <span>{row.workspaceName || row.workspaceId || "Workspace"}</span>
                      <span>Owner: {ownerLabel(row.assignee)}</span>
                      {row.timestamp ? <span>{row.timestamp}</span> : null}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {nextCursor && !loading ? (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => void loadMore()}
                className="rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-600"
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <SearchTranscriptModal
        open={Boolean(selected?.id)}
        sessionId={selected?.id || ""}
        workspaceId={null}
        centralInbox
        sessionWorkspaceId={selected?.workspaceId || null}
        previewLabel={selected ? `Owner: ${ownerLabel(selected.assignee)}` : ""}
        onClose={() => setSelected(null)}
      />
    </AppLayout>
  );
}
